import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge/Badge';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Button } from '@/components/ui/Button/Button';
import { adminFeedbackService } from '@/services/adminFeedbackService';

interface TicketRow {
  id: string;
  type: 'bug' | 'feature' | 'help';
  subject: string | null;
  body: string | null;
  status: string;
  user_email: string;
  org_name: string | null;
  created_at: string;
}

const typeVariants: Record<string, 'error' | 'default' | 'success'> = {
  bug: 'error',
  feature: 'default',
  help: 'success',
};

const statusVariants: Record<string, 'warning' | 'default' | 'success' | 'secondary'> = {
  open: 'warning',
  in_progress: 'default',
  resolved: 'success',
  closed: 'secondary',
};

export function TicketsList() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [tickets, setTickets] = React.useState<TicketRow[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [totalItems, setTotalItems] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [currentPageSize, setCurrentPageSize] = React.useState(15);
  const [filters, setFilters] = React.useState<Record<string, string>>({});

  const fetchTickets = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminFeedbackService.listTickets({
        type: filters.type || undefined,
        status: filters.status || undefined,
        page: currentPage,
        pageSize: currentPageSize,
      });
      setTickets(result.data);
      setTotalItems(result.total);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, currentPageSize, filters]);

  React.useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleFilter = (newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePaginate = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setCurrentPageSize(pageSize);
  };

  const columns = [
    {
      key: 'subject',
      title: 'Subject',
      render: (value: string | null, record: TicketRow) => (
        <span className="text-foreground font-medium">
          {value || record.body?.slice(0, 60) || '(no subject)'}
        </span>
      ),
    },
    {
      key: 'type',
      title: 'Type',
      render: (value: string) => (
        <Badge variant={typeVariants[value] || 'secondary'}>{value}</Badge>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (value: string) => (
        <Badge variant={statusVariants[value] || 'secondary'}>
          {value.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      key: 'user_email',
      title: 'User',
    },
    {
      key: 'org_name',
      title: 'Organization',
    },
    {
      key: 'created_at',
      title: 'Created',
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      key: 'actions',
      title: '',
      align: 'right' as const,
      render: (_: any, record: TicketRow) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/admin/feedback/${record.id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  const filterOptions = [
    {
      key: 'type',
      label: 'Type',
      options: [
        { label: 'Bug', value: 'bug' },
        { label: 'Feature', value: 'feature' },
        { label: 'Help', value: 'help' },
      ],
    },
    {
      key: 'status',
      label: 'Status',
      options: [
        { label: 'Open', value: 'open' },
        { label: 'In Progress', value: 'in_progress' },
        { label: 'Resolved', value: 'resolved' },
        { label: 'Closed', value: 'closed' },
      ],
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Support Tickets"
        description="View and manage all feedback tickets across organizations."
      >
        <Button variant="outline" size="sm" onClick={() => navigate('/admin/feedback/settings')}>
          Settings
        </Button>
      </PageHeader>

      {error && (
        <div className="mb-4 p-4 bg-error-50 border border-error-200 rounded-md">
          <div className="text-sm text-error-600">{error}</div>
        </div>
      )}

      <Table
        data={tickets}
        columns={columns}
        loading={loading}
        searchable={false}
        filters={filterOptions}
        pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: [10, 15, 25, 50] }}
        emptyText="No tickets yet"
        serverSide={true}
        onFilter={handleFilter}
        onPaginate={handlePaginate}
        totalItems={totalItems}
      />
    </PageContainer>
  );
}
